import React, { useState, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation, useHistory } from "react-router-dom";
import { selectOrg } from "../../models/organization/organizationSlice";
import { selectMyUser } from "../../models/users/myUserSlice";
import { statusToTitle } from "../../utils/helpers";
import { LocalStorage } from "../../utils/localStorage";
import { FilterableDashboard } from "./filterable-dashboard";
import { Dashboard } from "./dashboard";

import "./application.scss";

export const Applications = () => {
  const organization = useSelector(selectOrg);
  const myUser = useSelector(selectMyUser);
  const location = useLocation();
  const history = useHistory();

  // Create LocalStorage instances with user-specific baseKey (fi-slug/user-id)
  const { preferences, context } = useMemo(() => {
    const baseKey =
      organization?.slug && myUser?.userId
        ? `${organization.slug}/${myUser.userId}`
        : "shared";
    return {
      preferences: new LocalStorage(baseKey, false), // localStorage
      context: new LocalStorage(baseKey, true), // sessionStorage
    };
  }, [organization?.slug, myUser?.userId]);

  // Parse query params to determine which view to show
  const queryParams = new URLSearchParams(location.search);
  const isClassicView = queryParams.get("view") === "classic";

  // Initialize state from storage
  // viewFilter and includeArchived use localStorage (preferences)
  // search uses sessionStorage (context)
  const [search, setSearch] = useState(() => {
    return context.getItem("applications_search");
  });
  const [includeArchived, setIncludeArchived] = useState(() => {
    const stored = preferences.getItem("applications_includeArchived");
    return stored === "true";
  });
  const [viewFilter, setViewFilter] = useState(() => {
    return preferences.getItem("applications_viewFilter");
  });

  // Handlers from FilterableDashboard
  const [clearColumnFiltersHandler, setClearColumnFiltersHandler] = useState<
    (() => void) | null
  >(null);
  const [exportCsvHandler, setExportCsvHandler] = useState<(() => void) | null>(
    null
  );

  // Save state to storage whenever it changes
  // Preferences go to localStorage, context goes to sessionStorage
  useEffect(() => {
    context.setItem("applications_search", search);
  }, [search, context]);

  useEffect(() => {
    preferences.setItem(
      "applications_includeArchived",
      String(includeArchived)
    );
  }, [includeArchived, preferences]);

  useEffect(() => {
    preferences.setItem("applications_viewFilter", viewFilter);
  }, [viewFilter, preferences]);

  const clearAllFilters = () => {
    // Clear view dropdown to "All"
    setViewFilter("");
    // Clear search box
    setSearch("");
    // Clear sessionStorage (context) only - keep localStorage (preferences)
    context.removeItem("applications_search");
    // Also clear localStorage preferences (they'll be re-saved as empty by useEffect)
    preferences.setItem("applications_viewFilter", "");
    // Clear page query param from URL
    const newParams = new URLSearchParams(location.search);
    if (newParams.has("page")) {
      newParams.delete("page");
      history.replace({
        pathname: location.pathname,
        search: newParams.toString(),
      });
    }
    // Clear column filters in FilterableDashboard (if it's active)
    if (clearColumnFiltersHandler) {
      clearColumnFiltersHandler();
    }
  };

  const handleExportCsv = () => {
    if (exportCsvHandler) {
      exportCsvHandler();
    }
  };

  // Convert viewFilter to activeFilter format for FilterableDashboard
  const activeFilter = useMemo(() => {
    if (viewFilter === "assignedToMe") {
      return { assignedToMe: true, decisionStatus: undefined };
    }
    if (viewFilter) {
      return { assignedToMe: undefined, decisionStatus: viewFilter };
    }
    return {};
  }, [viewFilter]);

  if (!myUser) {
    return null;
  }

  return (
    <div id="application-dashboard" className="container-fluid">
      {/* Common Filter Controls - Extracted from dashboard.tsx */}
      <div className="application-table-control-container row justify-content-between">
        <div className="col-4">
          <div className="form-group row control-filter align-items-center">
            <label className="form-check-label px-3">View</label>
            <select
              className="custom-select col"
              style={{ cursor: "pointer" }}
              value={viewFilter}
              onChange={(e) => {
                setViewFilter(e.target.value);
              }}
            >
              <option value="">All</option>
              <option value="assignedToMe">Assigned to Me</option>
              {!organization.meta?.enableDecisioning &&
                Object.keys(statusToTitle).map((status) => {
                  return (
                    <option value={status} key={status}>
                      {statusToTitle[status]}
                    </option>
                  );
                })}
              {organization.meta?.enableDecisioning === true &&
                (organization.decisionStatuses || []).map((status) => {
                  return (
                    <option value={status} key={status}>
                      {status}
                    </option>
                  );
                })}
            </select>
            <div className="ml-4 form-check">
              <input
                className={`form-check-input`}
                style={{ cursor: "pointer" }}
                type="checkbox"
                checked={includeArchived}
                onChange={() => setIncludeArchived(!includeArchived)}
              />
              <label className="form-check-label">Include Archived</label>
            </div>
          </div>
        </div>
        {/* Search Bar */}
        <div className="col-4">
          <div className="d-flex justify-content-end">
            <div className="col">
              <input
                type="text"
                className="form-control"
                placeholder="Search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                }}
              />
            </div>
            <button
              type="button"
              className="btn btn-outline-primary ml-2"
              onClick={() =>
                history.push(`${location.pathname}/overview${location.search}`)
              }
              style={{ whiteSpace: "nowrap" }}
            >
              Overview
            </button>
            <button
              type="button"
              className="btn btn-outline-primary ml-2"
              onClick={clearAllFilters}
              style={{ whiteSpace: "nowrap" }}
            >
              Clear Filters
            </button>
            <button
              type="button"
              className="btn btn-outline-primary ml-2"
              onClick={handleExportCsv}
              title="Export to CSV"
              disabled={!exportCsvHandler}
            >
              <span className="oi oi-data-transfer-download"></span>
            </button>
          </div>
        </div>
      </div>

      {/* Render appropriate dashboard based on view parameter */}
      {isClassicView ? (
        <Dashboard
          search={search}
          viewFilter={viewFilter}
          includeArchived={includeArchived}
        />
      ) : (
        <FilterableDashboard
          search={search}
          activeFilter={activeFilter}
          includeArchived={includeArchived}
          setClearColumnFiltersHandler={setClearColumnFiltersHandler}
          setExportCsvHandler={setExportCsvHandler}
          fiSlug={organization?.slug}
          userId={myUser?.userId}
        />
      )}
    </div>
  );
};
